import React, { FC, useEffect, useState, useMemo } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Table, Card, Button, Space, Tag, Select } from "antd"
import { CreateEditCountryMultiplierModal } from "./create-edit-country-multiplier-modal"
import { CountryMultiplierDto } from "../store/types"
import { COUNTRY_CODES } from "utils/countryCodes"
import { gameNames } from "abstract/constants/game-ids"
import { createDateColumn, handleDeleteWithConfirm } from "../utils"
import { getGameOptionsWithNumberValues } from "components/gameSelector/gameOptions"

const { Option } = Select

export const CountryMultipliersTable: FC = observer(() => {
  const { dataStore: { countryGroupsStore } } = useStore()
  const [showModal, setShowModal] = useState(false)
  const [editingMultiplier, setEditingMultiplier] = useState<CountryMultiplierDto | null>(null)
  const [filterGameId, setFilterGameId] = useState<number | "global" | null>(null)
  const [filterType, setFilterType] = useState<"all" | "country" | "group" | "global">("all")

  const gameOptions = getGameOptionsWithNumberValues()

  useEffect(() => {
    countryGroupsStore.getAllCountryMultipliers()
    countryGroupsStore.getAllCountryGroups()
  }, [countryGroupsStore])

  const handleDelete = (multiplier: CountryMultiplierDto) => {
    handleDeleteWithConfirm(
      "country multiplier",
      () => countryGroupsStore.deleteCountryMultiplier(multiplier.id),
      "Country multiplier deleted",
      "Failed to delete country multiplier",
    )
  }

  const getScopeDisplay = (multiplier: CountryMultiplierDto) => {
    const tags: JSX.Element[] = []

    if (multiplier.gameId !== null) {
      const gameName = gameNames[multiplier.gameId]
      tags.push(
        <Tag key="game" color="blue">
          {multiplier.gameId}{gameName ? ` - ${gameName}` : ""}
        </Tag>,
      )
    } else {
      tags.push(
        <Tag key="game" color="default">
          Global
        </Tag>,
      )
    }

    if (multiplier.countryGroupId) {
      const group = countryGroupsStore.countryGroups.find(g => g.id === multiplier.countryGroupId)
      tags.push(
        <Tag key="group" color="green">
          {group ? group.name : multiplier.countryGroupId}
        </Tag>,
      )
    } else if (multiplier.countryCode) {
      const country = COUNTRY_CODES.find(c => c.code === multiplier.countryCode)
      tags.push(
        <Tag key="country" color="orange">
          {country ? `${country.name} (${multiplier.countryCode})` : multiplier.countryCode}
        </Tag>,
      )
    } else {
      tags.push(
        <Tag key="scope" color="default">
          Global
        </Tag>,
      )
    }

    return <Space wrap>{tags}</Space>
  }

  const filteredMultipliers = useMemo(() => {
    let filtered = countryGroupsStore.countryMultipliers

    if (filterGameId != null) {
      if (filterGameId === "global") {
        filtered = filtered.filter(m => m.gameId === null)
      } else {
        filtered = filtered.filter(m => m.gameId === filterGameId || m.gameId === null)
      }
    }

    if (filterType !== "all") {
      if (filterType === "country") {
        filtered = filtered.filter(m => m.countryCode !== null)
      } else if (filterType === "group") {
        filtered = filtered.filter(m => m.countryGroupId !== null)
      } else if (filterType === "global") {
        filtered = filtered.filter(m => m.countryCode === null && m.countryGroupId === null)
      }
    }

    return filtered
  }, [countryGroupsStore.countryMultipliers, filterGameId, filterType])

  const columns = [
    {
      title: "ID",
      key: "id",
      dataIndex: "id",
      width: 200,
    },
    {
      title: "Scope",
      key: "scope",
      width: 400,
      render: (_: any, record: CountryMultiplierDto) => getScopeDisplay(record),
    },
    {
      title: "Multiplier",
      key: "multiplier",
      dataIndex: "multiplier",
      width: 150,
      render: (value: number | string | null | undefined) => {
        const numValue = typeof value === "number" ? value : Number(value)

        return isNaN(numValue) ? "-" : numValue.toFixed(2)
      },
    },
    createDateColumn("Created At", "createdAt"),
    createDateColumn("Updated At", "updatedAt"),
    {
      title: "Actions",
      key: "actions",
      width: 150,
      fixed: "right" as const,
      render: (_: any, record: CountryMultiplierDto) => (
        <Button
          type="link"
          danger
          onClick={e => {
            e.stopPropagation()
            handleDelete(record)
          }}
        >
          Delete
        </Button>
      ),
    },
  ]

  return (
    <div>
      <Button
        type="primary"
        onClick={() => {
          setEditingMultiplier(null)
          setShowModal(true)
        }}
        style={{ marginBottom: 16 }}
      >
        Create Country Multiplier
      </Button>
      <Card title="Country Multipliers" size="small">
        <Space style={{ marginBottom: 16 }}>
          <Select
            placeholder="Filter by Game"
            allowClear
            style={{ width: 200 }}
            value={filterGameId}
            onChange={value => setFilterGameId(value)}
          >
            <Option value="global">Global</Option>
            {gameOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Filter by Type"
            style={{ width: 150 }}
            value={filterType}
            onChange={value => setFilterType(value)}
          >
            <Option value="all">All</Option>
            <Option value="country">Country</Option>
            <Option value="group">Group</Option>
          </Select>
        </Space>
        <Table
          dataSource={filteredMultipliers}
          columns={columns}
          rowKey="id"
          loading={countryGroupsStore.isLoading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: "max-content" }}
          onRow={record => ({
            style: { cursor: "pointer" },
            onClick: () => {
              setEditingMultiplier(record)
              setShowModal(true)
            },
          })}
        />
      </Card>
      <CreateEditCountryMultiplierModal
        visible={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingMultiplier(null)
        }}
        initialData={editingMultiplier}
      />
    </div>
  )
})
